import {useCallback, useMemo} from 'react'
import {useAnalytics} from '@github-ui/use-analytics'
import {useActiveRoute} from '../../../hooks/use-active-route'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import type {UnifiedBatchItem} from '../../../stores/slices/unified-batch'
import type {ManageBatchTab} from '../ManageBatchPanel'

/**
 * Panel-level telemetry for the unified Manage batch surface.
 *
 * Emits via `useAnalytics().sendAnalyticsEvent(name, target, payload)`,
 * matching the precedent set in
 * https://github.com/github/github-ui/pull/20679 for the legacy per-comment
 * suggestion-batch actions (`comments.commit_suggested_change_from_batch`,
 * `comments.add_suggested_change_to_batch`, etc.). Every event carries the
 * shared `commentKind` (`'ccr' | 'human' | 'mixed'`) and `surface`
 * (`useActiveRoute()`) dimensions so the Unified PR review batch dashboard
 * (#790) can slice the same way the legacy dashboards do.
 *
 * Call sites:
 *  - `onPanelOpen` is fired by the entry-point sub-issues (#795 per-comment
 *    `Add to batch`, #802 sticky-header `Manage batch (N)` button), not by
 *    the shell itself, because only the caller knows the entry surface.
 *  - `onPanelClose` is fired by the shell's Cancel/Discard footer buttons.
 *  - `onTabSelected` is fired by the shell's tab nav.
 *  - `onSubmit` / `onSubmitSuccess` / `onSubmitFailure` are fired by the
 *    tab-content sub-issues (#798 Commit suggestions, #799 Fix with Copilot).
 */
export interface ManageBatchTelemetry {
  onPanelOpen: (entrySurface: ManageBatchEntrySurface) => void
  onPanelClose: () => void
  onTabSelected: (tab: ManageBatchTab, selectionSource?: ManageBatchTabSelectionSource) => void
  onSubmit: (tab: ManageBatchTab) => void
  onSubmitSuccess: (tab: ManageBatchTab) => void
  onSubmitFailure: (tab: ManageBatchTab, error: unknown) => void
}

export type ManageBatchTabSelectionSource = 'tab_click' | 'disallowed_tab_nudge'

export type ManageBatchEntrySurface =
  | 'sticky_header_manage_batch_button'
  | 'header_manage_batch_button'
  | 'panel_switcher'
  | 'per_comment_add_to_batch'
  | 'per_comment_remove_from_batch'

const TARGET = 'UNIFIED_BATCH_PANEL'

type EventPayload = Record<string, unknown>

export function useManageBatchTelemetry(): ManageBatchTelemetry {
  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()
  const {items} = useUnifiedBatch()
  const commentKind = deriveBatchCommentKind(items)
  const batchSize = items.length

  const emit = useCallback(
    (eventName: string, extras: EventPayload = {}) => {
      sendAnalyticsEvent(eventName, TARGET, {surface, commentKind, batchSize, ...extras})
    },
    [sendAnalyticsEvent, surface, commentKind, batchSize],
  )

  const onPanelOpen = useCallback(
    (entrySurface: ManageBatchEntrySurface) => emit('unified_batch_panel.open', {entrySurface}),
    [emit],
  )
  const onPanelClose = useCallback(() => emit('unified_batch_panel.close'), [emit])
  const onTabSelected = useCallback(
    (tab: ManageBatchTab, selectionSource: ManageBatchTabSelectionSource = 'tab_click') =>
      emit('unified_batch_panel.tab_selected', {tab, selectionSource}),
    [emit],
  )
  const onSubmit = useCallback((tab: ManageBatchTab) => emit('unified_batch_panel.submit', {tab}), [emit])
  const onSubmitSuccess = useCallback(
    (tab: ManageBatchTab) => emit('unified_batch_panel.submit_success', {tab}),
    [emit],
  )
  const onSubmitFailure = useCallback(
    (tab: ManageBatchTab, error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      emit('unified_batch_panel.submit_failure', {tab, errorMessage})
    },
    [emit],
  )

  return useMemo(
    () => ({
      onPanelOpen,
      onPanelClose,
      onTabSelected,
      onSubmit,
      onSubmitSuccess,
      onSubmitFailure,
    }),
    [onPanelOpen, onPanelClose, onTabSelected, onSubmit, onSubmitSuccess, onSubmitFailure],
  )
}

/**
 * Derives the composite `commentKind` for a batch, matching the precedent in
 * `PullRequestFilesToolbar` for the legacy suggestion batch:
 *  - `'human'` — every item is from a human review (or batch is empty)
 *  - `'ccr'` — every item is from automated review (CCR)
 *  - `'mixed'` — batch contains both human and CCR items
 *
 * The 4-way composition split (`ccr-with-suggestion` / `ccr-without-suggestion` /
 * `human-with-suggestion` / `human-without-suggestion`) is the dimension the
 * Unified PR review batch dashboard (#790) ultimately wants, and is computable
 * from the same items at the call site or by extending this helper later. The
 * coarse `commentKind` matches what existing per-comment events emit, which
 * keeps cross-flow comparisons on the dashboard straightforward.
 */
function deriveBatchCommentKind(items: UnifiedBatchItem[]): 'ccr' | 'human' | 'mixed' {
  if (items.length === 0) return 'human'
  const hasCcr = items.some(item => item.kind.startsWith('ccr'))
  const hasHuman = items.some(item => item.kind.startsWith('human'))
  if (hasCcr && hasHuman) return 'mixed'
  if (hasCcr) return 'ccr'
  return 'human'
}
